/* eslint-disable camelcase */
import BasicDatePicker from '@components/form/date-picker/BasicDatePicker';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {
  ContainerGridStyle,
  InputFieldGrid
} from '@modules/home/main/modules-menu/module-search-filter/ModuleSearchFilterStyle';
import CollapsibleTable from '@modules/home/main/sub-modules-menu/collapsible-table/CollapsibleTable';
import {Box, Card, Grid, useTheme} from '@mui/material';
import FormConstants from '@shared/constants/FormConstants';
import {getPageData} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {useSnackbar} from 'notistack';
import React, {ReactElement, useEffect, useState, useCallback} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {BASE_RECORD_ID} from 'src/routes/BaseRoute';
import EncryptUtils from 'src/utils/EncryptUtils';
import {DataGridComponentDiv} from './BenefitsComponentStyle';
import AuthUtils from 'src/utils/AuthUtils';
import {useAppContext} from '@context/AppContext';
import {LoadingButton} from '@mui/lab';
import {CircularProgressDiv} from '@modules/public/main/login/LoginStyles';
import EmptyContent from '@components/empty-content/empty-content';
import {PulseLoader} from 'react-spinners';

interface BenefitsComponentProps {
  sectionUIData: any;
  sectionRowsData: any;
  changeLegacySystem?: string;
  onLegacySystemChange?: (value: string) => void;
}
const BenefitsComponent = (
  props: BenefitsComponentProps
): React.ReactElement => {
  const {
    appState: {applicationName}
  } = useAppContext();
  const {sectionUIData} = props;
  const theme = useTheme();
  const params: any = useParams();
  const [loader, setLoader] = useState<any>(false);
  const [BenefitsPayload, setBenefitsPayload] = useState({});
  const [data, setData] = useState([]);
  const [startDate, setStartDate] = useState<any>(null);
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const {
    control,
    handleSubmit,
    reset,
    formState: {errors}
  } = useForm({
    mode: 'onChange'
  });

  const record_id = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );

  const fetchData = async (values: any) => {
    if (
      params?.module_name &&
      params?.submodule_name &&
      sectionUIData?.sub_sections?.sql_data?.sql &&
      sectionUIData?.sub_sections?.sql_data?.variables
    ) {
      const encryptRequestData = EncryptUtils.encryptedData(values);

      const result = await getPageData(
        applicationName,
        params?.module_name,
        params?.submodule_name,
        {tk: encryptRequestData}
      );
      return result;
    }
  };

  const init = async () => {
    try {
      const values = {
        sql_data: sectionUIData?.sub_sections?.sql_data,
        record_id: record_id
      };
      setLoader(true);
      const res: any = await fetchData(values);
      setData(res?.data || []);
      setLoader(false);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setLoader(false);
    }
  };

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (dataObj: any) => {
    try {
      const start_date =
        dataObj?.start_date != null ? new Date(dataObj?.start_date) : '';
      const stop_date: any =
        dataObj?.stop_date != null ? new Date(dataObj.stop_date) : '';

      const startDate = start_date && start_date.toLocaleDateString('en-US');
      const endDate = stop_date && stop_date.toLocaleDateString('en-US');

      const lastDay =
        sectionUIData?.fields?.start_date?.monthYearFormatView.length > 0 &&
        dataObj?.stop_date != null &&
        new Date(stop_date?.getFullYear(), stop_date.getMonth() + 1, 0);
      const monthYearEndDate = lastDay && lastDay.toLocaleDateString('en-US');
      const values = {
        sql_data: sectionUIData?.sub_sections?.sql_data,
        record_id: record_id,
        start_month_year: startDate,
        end_month_year:
          sectionUIData?.fields?.start_date?.monthYearFormatView.length > 0
            ? monthYearEndDate
            : endDate,

        audit_sub_module_identifier: `${startDate} to ${
          sectionUIData?.fields?.start_date?.monthYearFormatView.length > 0
            ? `${monthYearEndDate}`
            : `${endDate}`
        }`,
        audit_identifier: AuthUtils.getAuditIdentifier(),
        audit_identifier_type: AuthUtils.getAuditIdentifierType(),
        audit_legacy_app: AuthUtils.getAuditLegacyApp()
      };
      setLoader(true);
      setBenefitsPayload(values);
      const res: any = await fetchData(values);
      setData(res?.data || []);
      setLoader(false);
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setLoader(false);
    }
  };

  const onReset = useCallback(() => {
    reset();
    init();
    setData([]);
    onSubmit({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderDatagridComponent = () => {
    return (
      <DataGridComponentDiv style={{marginLeft: '-1rem'}}>
        <CollapsibleTable
          sectionUIData={sectionUIData?.sub_sections}
          sectionRowsData={data}
          recordId={record_id}
          module={params.module_name}
          sub_module={params?.submodule_name}
          BenefitsPayload={BenefitsPayload}
        />
      </DataGridComponentDiv>
    );
  };

  const renderDateField = (inputName: string, item: any): ReactElement => {
    return (
      <Controller
        name={inputName}
        control={control}
        defaultValue={null}
        render={({field}) => (
          <BasicDatePicker
            label={item?.heading || ''}
            error={!!errors[inputName]}
            helperText={`${sectionUIData?.fields?.start_date?.heading?.[0]} is required`}
            field={field}
            monthYearFormatView={
              sectionUIData?.fields?.start_date?.monthYearFormatView.length > 0
                ? true
                : false
            }
            handleChange={(e: any) => {
              if (inputName === 'start_date') setStartDate(e); // Update start date state
            }}
            minDate={
              inputName === 'stop_date' || inputName === 'end_date'
                ? startDate
                : undefined
            }
          />
        )}
      />
    );
  };

  const renderSearchButton = () => {
    return (
      <LoadingButton
        type="submit"
        variant="contained"
        sx={{
          mr: 2
        }}>
        {t('PRIVATE.COMMON.SEARCH', {ns: 'private'})}
      </LoadingButton>
    );
  };

  const renderResetButton = () => {
    return (
      <LoadingButton
        onClick={onReset}
        type="reset"
        variant="outlined"
        sx={{
          mr: 2
        }}>
        {t('PRIVATE.COMMON.RESET', {ns: 'private'})}
      </LoadingButton>
    );
  };

  const renderMainFields = (): ReactElement => {
    return (
      <ContainerGridStyle container spacing={2}>
        {Object.entries(sectionUIData?.fields).map(
          (item: any, index: number) => {
            return (
              <Grid item key={index} xs={12} md={3}>
                {item[1].type === FormConstants.FIELD_TYPES.DATE
                  ? renderDateField(item[0], item[1])
                  : ''}
              </Grid>
            );
          }
        )}
        <Grid item>
          <Box sx={{mt: 1}}>
            {renderSearchButton()}
            {renderResetButton()}
          </Box>
        </Grid>
      </ContainerGridStyle>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Card sx={{p: 3, boxShadow: 5, borderRadius: '8px'}}>
        <InputFieldGrid>{renderMainFields()}</InputFieldGrid>
      </Card>
      {loader ? (
        <Card
          sx={{
            p: 3,
            mt: 3,
            boxShadow: 5,
            borderRadius: '8px',
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex'
          }}>
          <PulseLoader size={12} color={theme.palette.primary.main} />
        </Card>
      ) : (
        <Card sx={{p: 3, mt: 3, boxShadow: 5, borderRadius: '8px'}}>
          {data && data.length ? (
            renderDatagridComponent()
          ) : (
            <CircularProgressDiv height="300px">
              <EmptyContent sx={{pt: 1}} title="No Data" />
            </CircularProgressDiv>
          )}
        </Card>
      )}
    </form>
  );
};

export default BenefitsComponent;
